import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AudioPlayer from "./AudioPlayer";
import { useTranslation } from "react-i18next";
import { Music, Video, Image as ImageIcon } from "lucide-react";

type TalentMedia = {
  id: string;
  media_type: "audio" | "video" | "image";
  url: string;
  title: string | null;
  display_order: number;
};

interface TalentMediaGalleryProps {
  talentId: string;
  talentName: string;
}

export const TalentMediaGallery = ({ talentId, talentName }: TalentMediaGalleryProps) => {
  const { t } = useTranslation();

  const { data: mediaItems } = useQuery({
    queryKey: ["public-talent-media", talentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("talent_media")
        .select("*")
        .eq("talent_id", talentId)
        .order("display_order");
      if (error) throw error;
      return data as TalentMedia[];
    },
  });

  if (!mediaItems || mediaItems.length === 0) return null;

  const audioItems = mediaItems.filter((m) => m.media_type === "audio");
  const videoItems = mediaItems.filter((m) => m.media_type === "video");
  const imageItems = mediaItems.filter((m) => m.media_type === "image");

  return (
    <div className="space-y-8">
      {/* Audio Section */}
      {audioItems.length > 0 && (
        <div>
          <h2 className="text-sm uppercase tracking-wide font-medium mb-4 flex items-center gap-2 text-primary">
            <Music size={16} />
            {t("talentProfile.voiceDemo")}
          </h2>
          <div className="space-y-3">
            {audioItems.map((item) => (
              <div key={item.id}>
                <AudioPlayer
                  src={item.url}
                  title={item.title || `${talentName} - Demo`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video Section */}
      {videoItems.length > 0 && (
        <div>
          <h2 className="text-sm uppercase tracking-wide font-medium mb-4 flex items-center gap-2 text-primary">
            <Video size={16} />
            {t("talentProfile.videoDemo", "Video Demos")}
          </h2>
          <div className="space-y-4">
            {videoItems.map((item) => (
              <div key={item.id} className="space-y-2">
                {item.title && (
                  <p className="text-sm text-muted-foreground">{item.title}</p>
                )}
                <video
                  src={item.url}
                  controls
                  className="w-full rounded-lg bg-muted"
                  preload="metadata"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Gallery Section */}
      {imageItems.length > 0 && (
        <div>
          <h2 className="text-sm uppercase tracking-wide font-medium mb-4 flex items-center gap-2 text-primary">
            <ImageIcon size={16} />
            {t("talentProfile.gallery", "Gallery")}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {imageItems.map((item) => (
              <div key={item.id} className="aspect-square bg-muted overflow-hidden rounded-lg">
                <img
                  src={item.url}
                  alt={item.title || talentName}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
